/**
 * Дерево «Разделы каталога» по прототипу (`catalogTree()` в `dist/app.js`): корень раздела типа
 * (техника / запчасти), ветки раскрываются кнопкой ± отдельно от выбора раздела.
 * Чистые функции над состоянием хранилища — без Angular, покрыты node-спекой.
 */

/** Алиас крошки карточки модели (`cabinet/equipment/:uid`): подпись ставит сама карточка. */
export const EQUIPMENT_CARD_CRUMB = 'equipmentModel';

/** Алиас крошки карточки запчасти (`cabinet/parts/:uid`). */
export const PART_CARD_CRUMB = 'partItem';

/** Тип каталога с бэка (`catalog.catalog_type`, из 1С: 1 — техника, 2 — запчасти). */
export type CatalogType = 'TECH' | 'PART';

/** Узел `GET /catalog/tree…` (`CatalogTypedLazyDto`); `productCount` уже включает вложенные разделы. */
export interface CatalogNode {
  id: number;
  header: string;
  productCount: number;
  lastLevel: boolean;
  parentId?: number;
  catalogType?: CatalogType;
}

export interface TreeRow {
  node: CatalogNode;
  depth: number;
  open: boolean;
  /** Есть ли у узла дети: до загрузки — по `lastLevel`, после — по факту. */
  expandable: boolean;
}

/** Видимые строки дерева: обход от корня, в раскрытые узлы с загруженными детьми. */
export function visibleRows(rootId: number,
                            nodes: ReadonlyMap<number, CatalogNode>,
                            children: ReadonlyMap<number, readonly number[]>,
                            open: ReadonlySet<number>): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (id: number, depth: number) => {
    const node = nodes.get(id);
    if (!node) {
      return;
    }
    const loaded = children.get(id);
    const expandable = loaded ? loaded.length > 0 : !node.lastLevel;
    const isOpen = expandable && open.has(id) && !!loaded;
    rows.push({node, depth, open: isOpen, expandable});
    if (isOpen) {
      loaded.forEach(childId => walk(childId, depth + 1));
    }
  };
  walk(rootId, 0);
  return rows;
}

/** Путь от корня раздела до узла (включительно) по `parentId`; `null` — узел вне раздела или неизвестен. */
export function trailTo(id: number, rootId: number, nodes: ReadonlyMap<number, CatalogNode>): number[] | null {
  const trail: number[] = [];
  let current = nodes.get(id);
  while (current) {
    trail.unshift(current.id);
    if (current.id === rootId) {
      return trail;
    }
    current = current.parentId == null ? undefined : nodes.get(current.parentId);
  }
  return null;
}

/**
 * Цепочка `/catalog/parent-chain/{id}` (корень всего каталога → узел) без предков раздела:
 * от корня раздела до узла. `null` — узел из другого раздела (чужой тип или «Мастер каталог»).
 */
export function chainFromSection(chain: readonly {id: number}[], rootId: number): number[] | null {
  const start = chain.findIndex(item => item.id === rootId);
  return start < 0 ? null : chain.slice(start).map(item => item.id);
}

/** `?category=` → id раздела; пусто, мусор или сам корень — `null` (выбран корень). */
export function parseCategory(value: string | null | undefined, rootId: number): number | null {
  const id = Number(value);
  return value && Number.isInteger(id) && id > 0 && id !== rootId ? id : null;
}

/** Крошка пути каталога: раздел и ссылка на него в каталоге раздела (`?category=`). */
export interface CatalogTrailItem {
  label: string;
  routeLink: string;
  queryParams: {category: number};
}

/**
 * Путь каталога до раздела товара для крошек карточки из `catalogBreadcrumbs` ответа `/product/{uid}`.
 * Первый элемент — корень раздела («Техника» / «Запчасти»): в крошках его уже представляет
 * «Каталог техники» / «Каталог запчастей», поэтому он отбрасывается. Узлы без id или названия пропускаются.
 */
export function catalogTrail(crumbs: readonly {id?: number; header?: string}[] | null | undefined,
                             sectionLink: string): CatalogTrailItem[] {
  return (crumbs ?? [])
    .slice(1)
    .filter(crumb => crumb?.id != null && crumb.header)
    .map(crumb => ({label: crumb.header, routeLink: sectionLink, queryParams: {category: crumb.id}}));
}
